import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  ManagementReview,
  ManagementReviewListItem,
  CreateManagementReviewPayload,
  CompleteReviewPayload,
  CreateActionItemPayload,
  ReviewActionItem,
  AgendaSuggestion,
  ConsultantOverviewData,
} from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

interface ReviewFilters {
  status?: string;
}

function cleanFilters(filters: Record<string, string | undefined>): Record<string, string> {
  const cleaned: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useManagementReviews(filters: ReviewFilters = {}) {
  const cleaned = cleanFilters(filters as Record<string, string | undefined>);
  return useQuery({
    queryKey: queryKeys.managementReviews.list(cleaned),
    queryFn: async (): Promise<ManagementReviewListItem[]> => {
      const response = await apiClient.get<ManagementReviewListItem[]>(
        '/api/v1/compliance/reviews/',
        { params: cleaned }
      );
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useManagementReview(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.managementReviews.detail(id),
    queryFn: async (): Promise<ManagementReview> => {
      const response = await apiClient.get<ManagementReview>(
        `/api/v1/compliance/reviews/${id}/`
      );
      return response.data;
    },
    enabled,
  });
}

export function useAgendaSuggestions(enabled = true) {
  return useQuery({
    queryKey: queryKeys.managementReviews.agendaSuggestions(),
    queryFn: async (): Promise<AgendaSuggestion[]> => {
      const response = await apiClient.get<AgendaSuggestion[]>(
        '/api/v1/compliance/reviews/agenda-suggestions/'
      );
      return response.data;
    },
    enabled,
    staleTime: FIVE_MINUTES,
  });
}

export function useConsultantOverview(enabled = true) {
  return useQuery({
    queryKey: queryKeys.consultantOverview.data(),
    queryFn: async (): Promise<ConsultantOverviewData> => {
      const response = await apiClient.get<ConsultantOverviewData>(
        '/api/v1/compliance/consultant-overview/'
      );
      return response.data;
    },
    enabled,
    staleTime: FIVE_MINUTES,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateManagementReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateManagementReviewPayload): Promise<ManagementReview> => {
      const response = await apiClient.post<ManagementReview>(
        '/api/v1/compliance/reviews/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.managementReviews.all });
    },
  });
}

export function useCompleteReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: CompleteReviewPayload;
    }): Promise<ManagementReview> => {
      const response = await apiClient.post<ManagementReview>(
        `/api/v1/compliance/reviews/${id}/complete/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.managementReviews.all });
    },
  });
}

export function useCreateActionItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reviewId,
      data,
    }: {
      reviewId: number;
      data: CreateActionItemPayload;
    }): Promise<ReviewActionItem> => {
      const response = await apiClient.post<ReviewActionItem>(
        `/api/v1/compliance/reviews/${reviewId}/action-items/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.managementReviews.all });
    },
  });
}

export function useUpdateActionItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reviewId,
      itemId,
      data,
    }: {
      reviewId: number;
      itemId: number;
      data: Partial<ReviewActionItem>;
    }): Promise<ReviewActionItem> => {
      const response = await apiClient.patch<ReviewActionItem>(
        `/api/v1/compliance/reviews/${reviewId}/action-items/${itemId}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.managementReviews.all });
    },
  });
}

export function useUpdateManagementReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateManagementReviewPayload>;
    }): Promise<ManagementReview> => {
      const response = await apiClient.patch<ManagementReview>(
        `/api/v1/compliance/reviews/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.managementReviews.all });
    },
  });
}
